import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { setFormatRequest, setIsEmojiPickerOpen } from '@common/reducers/ScheduledMessageEditorReducer';
import {
  Code,
  CodeOff,
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  StrikethroughS
} from '@mui/icons-material';
import { ButtonGroup, Divider, IconButton, Stack, Tooltip } from '@mui/material';
import React from 'react';
import { usePortalContainer } from '@/common/hooks/use-portal-container';
import { IoMdCloseCircle } from 'react-icons/io';
import { MdInsertEmoticon } from 'react-icons/md';

interface MessageEditorFontFormatButtonsProps {
  emojiButtonRef: React.RefObject<HTMLButtonElement>;
  onFormatRequest?: (symbol: '*' | '~' | '_' | '```' | '`' | 'bullet' | 'number' | 'quote') => void;
  onEmojiPickerToggle?: () => void;
}

// The editor lives inside a Dialog (z 20000 — the schedule-message and campaign
// editors alike), but MUI portals a tooltip to <body> at its default z 1500, so
// every toolbar tooltip rendered BEHIND the dialog and never showed. Raised the
// way the labels poppers raise theirs. Shared with the Save as template icon.
//
// And INTO the portal container of the tree it lives in: in the extension the
// editor renders inside a shadow root, and a popper portaled to WhatsApp's own
// <body> is outside it — unstyled (no background, no z-index), i.e. invisible.
// On the website the container is <body>, as before.
export function useEditorToolbarTooltipSlotProps() {
  const container = usePortalContainer();
  // MUI types `container` as an Element; the hook can hand back a ShadowRoot
  // (its fallback), which React portals into just the same.
  return React.useMemo(() => ({ popper: { container: container as unknown as HTMLElement, sx: { zIndex: 999999 } } }), [container]);
}

export const MessageEditorFontFormatButtons: React.FC<MessageEditorFontFormatButtonsProps> = ({
  emojiButtonRef,
  onFormatRequest,
  onEmojiPickerToggle,
}) => {
  const dispatch = useAppDispatch();
  const tooltipSlotProps = useEditorToolbarTooltipSlotProps();
  
  // Read state directly from Redux
  const isEmojiPickerOpen = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isEmojiPickerOpen);
  const currentListMode = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.currentListMode);
  const isInListMode = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.isInListMode);
  
  const activeFormats = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.activeFormats);
  
  // Format function that dispatches to Redux
  const formatBySymbol = (symbol: '*' | '~' | '_' | '```' | '`' | 'bullet' | 'number' | 'quote') => {
    
    // Use direct callback if available to preserve selection
    if (onFormatRequest) {
      onFormatRequest(symbol);
    } else {
      dispatch(setFormatRequest(symbol));
    }
  };
  
  // Emoji picker toggle function
  const toggleEmojiPicker = () => {
    // Use the parent's toggle function if provided (which includes cursor position storage)
    if (onEmojiPickerToggle) {
      onEmojiPickerToggle();
    } else {
      dispatch(setIsEmojiPickerOpen(!isEmojiPickerOpen));
    }
  };

  const iconButtonStyles = {
    transition: 'all 0.2s ease-in-out',
    minWidth: '36px',
    height: '36px',
    borderRadius: '6px',
    '&:hover': {
      transform: 'scale(1.05)',
    },
    '&:active': {
      transform: 'scale(0.95)',
    },
  };

  const activeIconButtonStyles = {
    ...iconButtonStyles,
    backgroundColor: 'rgba(0, 168, 132, 0.1)', // Light WhatsApp green background
    color: 'var(--color-primary)',
    '&:hover': {
      transform: 'scale(1.05)',
      backgroundColor: 'rgba(0, 168, 132, 0.2)',
    },
  };

  const textFormatButtons = [
    {
      symbol: '*' as const,
      icon: <FormatBold fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Bold (Ctrl+B)',
      'aria-label': 'Format text as bold',
    },
    {
      symbol: '_' as const,
      icon: <FormatItalic fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Italic (Ctrl+I)',
      'aria-label': 'Format text as italic',
    },
    {
      symbol: '~' as const,
      icon: <StrikethroughS fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Strikethrough (Ctrl+Shift+S)',
      'aria-label': 'Format text with strikethrough',
    },
  ];

  const codeFormatButtons = [
    {
      symbol: '`' as const,
      icon: <CodeOff fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Inline Code (Ctrl+Shift+C)',
      'aria-label': 'Format text as inline code',
    },
    {
      symbol: '```' as const,
      icon: <Code fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Code Block (Ctrl+Shift+M)',
      'aria-label': 'Format text as code block',
    },
  ];

  const listButtons = [
    {
      symbol: 'bullet' as const,
      icon: <FormatListBulleted fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Bulleted List (Ctrl+Shift+8)',
      'aria-label': 'Create bulleted list',
    },
    {
      symbol: 'number' as const,
      icon: <FormatListNumbered fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
      tooltip: 'Numbered List (Ctrl+Shift+7)',
      'aria-label': 'Create numbered list',
    },
  ];

  const quoteButton = {
    symbol: 'quote' as const,
    icon: <FormatQuote fontSize="small" sx={{ color: 'var(--color-primary)' }} />,
    tooltip: 'Quote (Ctrl+Shift+>)',
    'aria-label': 'Format as quote',
  };

  type ButtonType = {
    symbol: '*' | '~' | '_' | '```' | '`' | 'bullet' | 'number' | 'quote';
    icon: React.ReactElement;
    tooltip: string;
    'aria-label': string;
  };

  const getIsActive = (symbol: string) => {
    switch (symbol) {
      case '*': return activeFormats?.bold;
      case '_': return activeFormats?.italic;
      case '~': return activeFormats?.strike;
      case '`': return activeFormats?.code;
      case '```': return activeFormats?.codeBlock;
      case 'quote': return activeFormats?.quote;
      case 'bullet': return isInListMode && currentListMode === 'bullet';
      case 'number': return isInListMode && currentListMode === 'number';
      default: return false;
    }
  };

  const renderButtonGroup = (buttons: ButtonType[], groupLabel: string) => (
    <ButtonGroup 
      variant="text" 
      size="small"
      sx={{ 
        '& .MuiButtonGroup-grouped': {
          minWidth: '36px',
          border: 'none',
          '&:not(:last-of-type)': {
            borderRight: 'none',
          },
        },
      }}
      aria-label={groupLabel}
    >
      {buttons.map((button) => {
        // Determine if this button should be active
        const isActive = getIsActive(button.symbol);
        
        return (
          <Tooltip key={button.symbol} title={button.tooltip} arrow placement="top" slotProps={tooltipSlotProps}>
            <IconButton
              sx={{
                ...(isActive ? activeIconButtonStyles : iconButtonStyles),
                borderRadius: '6px',
                margin: '0',
                padding: '4px',
              }}
              onClick={() => {
                formatBySymbol(button.symbol);
              }}
              aria-label={button['aria-label']}
              aria-pressed={isActive}
              onMouseDown={(e) => e.preventDefault()}
              size="small"
            >
              {button.icon}
            </IconButton>
          </Tooltip>
        );
      })}
    </ButtonGroup>
  );

  return (
    <Stack 
      direction="row" 
      sx={{ 
        justifyContent: 'flex-start',
        alignItems: 'center',
        // 2px, not 4px. Measured in the campaign wizard at 1920: this group's natural
        // width was ~402px against 393px of room, so the emoji button wrapped and pushed
        // Add files/Templates off the toolbar line. The 8 inter-item gaps (16px) plus the
        // dividers' side margins (2px→1px, 8px) buy back 24px — the group now measures
        // 376px and the whole toolbar is one 53px row down to a ~600px editor. The groups
        // are already separated by dividers, so the tighter gap costs no legibility.
        gap: 0.25,
        padding: '8px 12px',
        // Wrap to a second row when the row can't fit (e.g. the campaign wizard editor at
        // phone widths) instead of spilling the trailing icons past the edge. No effect where
        // the toolbar already fits on one line. This is deliberately the ONLY thing in the
        // toolbar that wraps — see the nowrap note in the parent header component.
        flexWrap: 'wrap',
        minWidth: 0,
        flexShrink: 1,
      }}
    >
      {/* Text Formatting Group */}
      {renderButtonGroup(textFormatButtons, "Text formatting")}
      
      <Divider 
        orientation="vertical" 
        flexItem 
        sx={{ 
          height: '24px', 
          alignSelf: 'center',
          margin: '0 1px',
        }} 
      />
      
      {/* Code Formatting Group */}
      {renderButtonGroup(codeFormatButtons, "Code formatting")}
      
      <Divider 
        orientation="vertical" 
        flexItem 
        sx={{ 
          height: '24px', 
          alignSelf: 'center',
          margin: '0 1px',
        }} 
      />
      
      {/* List Formatting Group */}
      {renderButtonGroup(listButtons, "List formatting")}
      
      <Divider 
        orientation="vertical" 
        flexItem 
        sx={{ 
          height: '24px', 
          alignSelf: 'center',
          margin: '0 1px',
        }} 
      />
      
      {/* Quote Button */}
      <Tooltip title={quoteButton.tooltip} arrow placement="top" slotProps={tooltipSlotProps}>
        <IconButton
          sx={iconButtonStyles}
          onClick={() => {
            formatBySymbol(quoteButton.symbol);
          }}
          aria-label={quoteButton['aria-label']}
          onMouseDown={(e) => e.preventDefault()}
          size="small"
        >
          {quoteButton.icon}
        </IconButton>
      </Tooltip>
      
      <Divider 
        orientation="vertical" 
        flexItem 
        sx={{ 
          height: '24px', 
          alignSelf: 'center',
          margin: '0 1px',
        }} 
      />
      
      {/* Emoji Picker */}
      <Tooltip 
        title={isEmojiPickerOpen ? 'Close emoji picker (Ctrl+;)' : 'Open emoji picker (Ctrl+;)'} 
        arrow 
        placement="top"
        slotProps={tooltipSlotProps}
      >
        <IconButton
          sx={{
            ...iconButtonStyles,
            transform: 'scale(1.05)',
          }}
          ref={emojiButtonRef}
          onClick={toggleEmojiPicker}
          focusRipple={true}
          aria-label={isEmojiPickerOpen ? 'Close emoji picker' : 'Open emoji picker'}
          size="small"
        >
          {isEmojiPickerOpen ? 
            <IoMdCloseCircle style={{ color: 'var(--color-primary)' }} /> : 
            <MdInsertEmoticon style={{ color: 'var(--color-primary)' }} />
          }
        </IconButton>
      </Tooltip>
    </Stack>
  );
};